/**
 * Booking money math — pure, Deno-importable, client previews + server truth.
 * Vault: DATAMODEL § bookings · Business Logic §5 · money-math.md.
 */
import { GIFTPASS_VALUE_PHP } from "./giftpass.ts";

export interface BookingAmountsInput {
  /** promo_price of the chosen service variant — the ACTIVE price. */
  variantPromoPhp: number;
  /** promo_price of each selected add-on. */
  addonPromosPhp?: number[];
  /** Stacked GiftPasses applied (0 for online/cash bookings). */
  giftpassCount?: number;
  /**
   * Long Distance Surcharge from `applyDistancePricing` — part of gross.
   * Recomputed server-side from a server-computed road distance; a client
   * preview of it is never trusted (SECURITY §5).
   *
   * There is no minimum-price component: the distance minimum is an
   * ELIGIBILITY filter on which services may be selected, not a floor that
   * inflates the bill (Distance Conditions, August 3 2026 — OQ-42).
   */
  surchargePhp?: number;
  /**
   * Promo-code deduction (₱, already resolved by the caller). Gift Pass XOR
   * Promo Code — the caller enforces the exclusivity; the math here simply
   * deducts whatever it's handed, capped at gross like the GiftPass.
   */
  promoDeductionPhp?: number;
}

export interface BookingAmounts {
  /** Session promo price + add-ons (DATAMODEL bookings.gross_amount). */
  grossAmountPhp: number;
  /** N × 299 applied — stored even when it exceeds gross (remainder forfeited). */
  giftpassAmountPhp: number;
  /** What the client still owes. Never negative — no change on GiftPass. */
  amountDuePhp: number;
  /**
   * Commission is ALWAYS on the full session promo price — including sessions
   * paid wholly/partly by GiftPass; never on amount_due (PRD §6.5, money-math.md §1).
   * Add-on commission treatment is NOT defined in the vault → variant promo only
   * for now (Open Item for Luigi).
   */
  commissionBasePhp: number;
}

export function computeBookingAmounts(input: BookingAmountsInput): BookingAmounts {
  const {
    variantPromoPhp,
    addonPromosPhp = [],
    giftpassCount = 0,
    surchargePhp = 0,
    promoDeductionPhp = 0,
  } = input;
  if (variantPromoPhp < 0) throw new RangeError("variant promo cannot be negative");
  if (addonPromosPhp.some((p) => p < 0)) throw new RangeError("add-on promo cannot be negative");
  if (giftpassCount < 0 || !Number.isInteger(giftpassCount)) {
    throw new RangeError("giftpassCount must be a non-negative integer");
  }
  if (surchargePhp < 0) throw new RangeError("surcharge cannot be negative");
  if (promoDeductionPhp < 0) throw new RangeError("promo deduction cannot be negative");

  const gross = round2(variantPromoPhp + addonPromosPhp.reduce((a, b) => a + b, 0) + surchargePhp);
  const deduction = giftpassCount * GIFTPASS_VALUE_PHP; // ₱299 fixed, stackable
  // deduction ≥ gross → due 0, remainder forfeited (no change) — Business Logic §5
  const amountDue = round2(Math.max(0, gross - deduction - promoDeductionPhp));

  return {
    grossAmountPhp: gross,
    giftpassAmountPhp: round2(deduction),
    amountDuePhp: amountDue,
    // Commission stays on the FULL service promo — PRD §6.5. The Long Distance
    // Surcharge is NOT commission-bearing (Leon, 2026-08-03 — OQ-42; a working
    // decision, not a sourced rule, so Luigi should countersign before payroll
    // runs on real money).
    commissionBasePhp: round2(variantPromoPhp),
  };
}

/**
 * Therapist commission for one session: commission_base × the therapist's own
 * staff.commission_rate (per-staff, default 0.500 — NEVER a hardcoded literal;
 * money-math.md §2).
 */
export function computeCommission(commissionBasePhp: number, commissionRate: number): number {
  if (commissionBasePhp < 0) throw new RangeError("commission base cannot be negative");
  if (commissionRate < 0 || commissionRate > 1) {
    throw new RangeError("commission rate must be within [0, 1]");
  }
  return round2(commissionBasePhp * commissionRate);
}

/** Money stays 2-decimal on the wire and in TS — never floats drifting (money-math.md §5). */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
